import { saveAs } from 'file-saver'
import React, { useCallback, useMemo } from 'react'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'

// UI
import { NonFormUpload } from '@/components/Elements'
import { Box, Button, Flex, HStack, Heading, Spacer } from '@chakra-ui/react'
import { MdDeleteOutline, MdOutlineCheckCircle } from 'react-icons/md'
import { toast } from 'react-toastify'

//Hooks
import { hideLoadingOverlay, showLoadingOverlay } from '@/stores/ui/loadingOverlaySlice'

//Helper
import { createExcelBlob } from '@/utils/fileHelper'

import { useInitialTableState } from '@/components/Table/components/useInitialTableState'
import FullTable from '@/components/Table/FullTable/FullTable'
import { convertTableStateToQuery } from '@/utils/tableHelper'
import { createColumnHelper } from '@tanstack/react-table'
import useVendor from '../useVendor'
import { FilterType } from '@/components/Table/components/Filter/TableFilter'
import { LoadingSection } from '@/components/Elements'

const AdminVendorListPage = () => {
  // Table State
  const [storedTableState, setStoredTableState] = useInitialTableState(`table_state:vendor`, {})
  const query = convertTableStateToQuery(storedTableState)
  // Hooks

  const { useFetchAllVendors, useRemoveVendor, useUploadVendors, useUpdateVendor } = useVendor()
  const { data: fetchVendorResponse, isLoading } = useFetchAllVendors(query)
  const { data: allFetchVendorResponse } = useFetchAllVendors({ page: 1, pageSize: 0 }) // Page Size 0 means get all

  const dispatch = useDispatch()
  const navigate = useNavigate()
  const columnHelper = createColumnHelper()

  const removeVendor = useRemoveVendor()
  const uploadVendor = useUploadVendors()
  const updateVendor = useUpdateVendor()

  const handleDownload = () => {
    try {
      const excelData = allFetchVendorResponse.data.map((row) => ({
        name: row.name,
        supplierNumber: row.supplierNumber,
        components: row.allComponents,
        r4b: row.r4b ? 'yes' : '',
        contactPersonName: row.contactPersonName,
        contactPersonEmail: row.contactPersonEmail,
        remarks: row.remarks,
        projectReference: row.projectReference,
        address: row.address,
        BRNumber: row.BRNumber,
        contactNumber: row.contactNumber,
        status: row.status,
      }))
      const excelBlob = createExcelBlob(excelData)
      saveAs(excelBlob, 'Vendors.xlsx')
    } catch (err) {
      console.log(err)
    }
  }
  // Handler
  const handleAddVendor = () => {
    navigate('/admin/vendors/create')
  }

  const handleOnRowClick = ({ original }) => {
    if (!original?._id) {
      return
    }
    navigate(`/admin/vendors/${original?._id}`)
  }

  const handleDelete = useCallback(
    (id) => {
      removeVendor.mutate(id, {
        onSuccess: () => {},
      })
    },
    [removeVendor]
  )

  const handleActivate = useCallback(
    async (id) => {
      try {
        dispatch(showLoadingOverlay())

        await updateVendor.mutateAsync({ id, data: { isDeleted: false } })
      } catch (error) {
        console.log(error)
      } finally {
        dispatch(hideLoadingOverlay())
      }
    },
    [dispatch, updateVendor]
  )

  const handleUpload = (file) => {
    if (!file) {
      toast.error('No File Uploaded')
      return
    }
    dispatch(showLoadingOverlay())
    uploadVendor.mutate(file, {
      onSettled: () => {
        dispatch(hideLoadingOverlay())
      },
    })
  }
  const defaultColumns = useMemo(
    () => [
      columnHelper.display({
        id: 'action',
        size: 100,
        enableHiding: false,
        enableColumnFilter: false,
        cell: ({ row: { original } }) => {
          return (
            <>
              {original?.status === 'active' && (
                <Flex _hover={{ cursor: 'pointer' }} color="gray.400" justifyContent={'center'} w="100%">
                  <MdDeleteOutline
                    size={24}
                    onClick={(e) => {
                      e.stopPropagation()
                      handleDelete(original._id)
                    }}
                  />
                </Flex>
              )}
              {original?.status === 'inactive' && (
                <Flex _hover={{ cursor: 'pointer' }} color="green.400" justifyContent={'center'} w="100%">
                  <MdOutlineCheckCircle
                    size={24}
                    onClick={(e) => {
                      e.stopPropagation()
                      handleActivate(original._id)
                    }}
                  />
                </Flex>
              )}
            </>
          )
        },
      }),

      columnHelper.accessor('name', {
        header: () => <span>Supplier Name</span>,
      }),
      columnHelper.accessor('allComponents', {
        header: () => <span>Category</span>,
      }),
      columnHelper.accessor('firstContactPersonName', {
        header: () => <span>Main Contact Person</span>,
      }),
      columnHelper.accessor('firstContactPersonEmail', {
        header: () => <span>Main Contact Email</span>,
      }),

      columnHelper.accessor('status', {
        meta: {
          filterVariant: FilterType.MULTI_SELECT,
          filterOptions: [
            { label: 'Active', value: 'active' },
            { label: 'Inactive', value: 'inactive' },
            { label: 'Temporary', value: 'temporary' },
          ],
        },
      }),
    ],
    [columnHelper, handleActivate, handleDelete]
  )

  if (isLoading) {
    return <LoadingSection />
  }

  return (
    <>
      <Box shadow="md" borderWidth="1px" bg="white" w="100%" mb="auto">
        <Flex p={5} borderBottom="1px solid #d8dbe0">
          <Heading>Vendors</Heading>
          <HStack mx="5">
            <Button colorScheme="gray" onClick={handleDownload}>
              Download
            </Button>
            <NonFormUpload uploadHandler={handleUpload} />
          </HStack>
          <Spacer />
          <Button colorScheme="blue" onClick={handleAddVendor}>
            Create
          </Button>
        </Flex>
        <Box p={5} overflow="scroll">
          <FullTable
            data={fetchVendorResponse?.data ? fetchVendorResponse.data : []}
            rowCount={fetchVendorResponse?.count}
            columnDef={defaultColumns}
            state={storedTableState}
            setState={setStoredTableState}
            handleRowClick={handleOnRowClick}
          />
        </Box>
      </Box>
    </>
  )
}

export default AdminVendorListPage
